import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, FormControlLabel, Checkbox, Collapse, CircularProgress, useTheme
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Timeline table (by day or by month): totals per period with drill-down.
// The caller supplies the rows already computed plus a loader for each row's breakdown.

const DETAIL_PAGE_SIZE = 10; // entries per page inside an expanded period

// Shades with enough text contrast in both themes (plain green/red do not pass)
const VALUE_COLORS = {
  light: { positive: '#157a55', negative: '#b3261e' },
  dark: { positive: '#4ade80', negative: '#ff8a80' },
};

export const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

const formatSigned = (value) => `${value > 0 ? '+' : ''}${formatNumber(value)}`;

const DETAIL_ROW = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
  px: 1,
  py: 0.4,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

// One column of the breakdown (money in or money out), paginated
const DetailSection = ({ title, entries, color }) => {
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [entries]);

  const pageCount = Math.max(1, Math.ceil(entries.length / DETAIL_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * DETAIL_PAGE_SIZE;
  const visible = entries.slice(offset, offset + DETAIL_PAGE_SIZE);

  return (
    <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
      <Box sx={{ ...DETAIL_ROW, py: 0.5 }}>
        <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 700, color, letterSpacing: 0.3 }}>
          {title.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{entries.length}</Typography>
      </Box>

      {!entries.length ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', px: 1, py: 0.5 }}>
          Nenhum lançamento
        </Typography>
      ) : (
        visible.map((entry) => (
          <Box key={entry.id} sx={DETAIL_ROW}>
            <Typography
              sx={{ flex: 1, minWidth: 0, fontSize: 11.5 }}
              noWrap
              title={`${entry.primary} ${entry.secondary || ''}`}
            >
              {entry.primary}
              {entry.secondary && (
                <Box component="span" sx={{ color: 'text.secondary', ml: 0.75 }}>· {entry.secondary}</Box>
              )}
            </Typography>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color, whiteSpace: 'nowrap' }}>
              {formatNumber(entry.value)}
            </Typography>
          </Box>
        ))
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_, p) => setPage(p)}
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

const Breakdown = ({ detail, colors }) => {
  if (!detail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (detail.failed) {
    return (
      <Typography sx={{ fontSize: 12, color: 'error.main', py: 1.5, pl: 4 }}>
        Não foi possível carregar a composição deste período.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 4, pr: 1, py: 0.5 }}>
      <DetailSection title="Entradas" entries={detail.incomes || []} color={colors.positive} />
      <DetailSection title="Saídas" entries={detail.expenses || []} color={colors.negative} />
    </Box>
  );
};

export default function TimelineTable({
  title,
  rows = [],
  totals,
  periodColumnLabel = 'Data',
  unitLabel = ['período', 'períodos'],
  pageSize = 15,
  allowHideEmpty = false,
  loadDetail,
  emptyLabel = 'Sem movimentação no período',
}) {
  const theme = useTheme();
  const colors = VALUE_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'];
  const muted = theme.palette.text.disabled;

  const [hideEmpty, setHideEmpty] = useState(false);
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);
  const [details, setDetails] = useState({}); // cache per row: undefined = loading

  const toggleRow = useCallback(async (row) => {
    if (openRow === row.id) return setOpenRow(null);
    setOpenRow(row.id);
    if (details[row.id]) return;
    try {
      const detail = await loadDetail(row);
      setDetails((current) => ({ ...current, [row.id]: detail }));
    } catch (err) {
      console.error('Erro ao buscar composicao do periodo', err);
      setDetails((current) => ({ ...current, [row.id]: { failed: true } }));
    }
  }, [openRow, details, loadDetail]);

  const filtered = useMemo(() => (
    hideEmpty ? rows.filter((r) => r.incomes !== 0 || r.expenses !== 0) : rows
  ), [rows, hideEmpty]);

  useEffect(() => { setPage(1); setOpenRow(null); }, [hideEmpty, rows]);

  // A new period invalidates the cached breakdowns
  useEffect(() => { setDetails({}); }, [rows]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
        {emptyLabel}
      </Typography>
    );
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * pageSize;
  const visible = filtered.slice(offset, offset + pageSize);

  const valueColor = (value) => (value === 0 ? muted : value > 0 ? colors.positive : colors.negative);

  const cell = { fontSize: 13, whiteSpace: 'nowrap' };
  const headCell = { fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        {allowHideEmpty && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>Ocultar {unitLabel[1]} sem movimento</Typography>}
          />
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>{periodColumnLabel}</TableCell>
              <TableCell align="right" sx={headCell}>Entradas</TableCell>
              <TableCell align="right" sx={headCell}>Saídas</TableCell>
              <TableCell align="right" sx={headCell}>Resultado</TableCell>
              <TableCell align="right" sx={headCell}>Saldo acumulado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const hasMovement = row.incomes !== 0 || row.expenses !== 0;
              const isOpen = openRow === row.id;
              return [
                <TableRow
                  key={row.id}
                  hover={hasMovement}
                  onClick={hasMovement ? () => toggleRow(row) : undefined}
                  sx={{ cursor: hasMovement ? 'pointer' : 'default' }}
                >
                  <TableCell sx={{ ...cell, color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ChevronRightIcon
                        sx={{
                          fontSize: 18,
                          visibility: hasMovement ? 'visible' : 'hidden',
                          transform: isOpen ? 'rotate(90deg)' : 'none',
                          transition: 'transform 160ms',
                        }}
                      />
                      {row.label}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cell, color: row.incomes ? colors.positive : muted }}>
                    {formatNumber(row.incomes)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cell, color: row.expenses ? colors.negative : muted }}>
                    {row.expenses ? `-${formatNumber(row.expenses)}` : formatNumber(0)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cell, color: valueColor(row.result) }}>
                    {formatSigned(row.result)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: valueColor(row.runningBalance) }}>
                    {formatNumber(row.runningBalance)}
                  </TableCell>
                </TableRow>,
                <TableRow key={`${row.id}-detail`}>
                  <TableCell colSpan={5} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} unmountOnExit>
                      {/* Slightly distinct background so the breakdown reads as nested */}
                      <Box sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)' }}>
                        <Breakdown detail={details[row.id]} colors={colors} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>,
              ];
            })}

            {!visible.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ ...cell, color: 'text.secondary' }}>
                  Nenhum {unitLabel[0]} com movimento no período
                </TableCell>
              </TableRow>
            )}

            {!!totals && (
              <TableRow>
                <TableCell sx={{ ...cell, fontWeight: 700 }}>Total do período</TableCell>
                <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: colors.positive }}>
                  {formatNumber(totals.incomes)}
                </TableCell>
                <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: colors.negative }}>
                  -{formatNumber(totals.expenses)}
                </TableCell>
                <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: valueColor(totals.result) }}>
                  {formatSigned(totals.result)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {filtered.length} {filtered.length === 1 ? unitLabel[0] : unitLabel[1]}
        </Typography>
        {pageCount > 1 && (
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_, p) => setPage(p)}
            siblingCount={0}
            boundaryCount={1}
          />
        )}
      </Box>
    </Box>
  );
}
